import { useQuery } from '@tanstack/react-query';
import { activityOrderId } from '@/lib/activityRowId';
import { supabase } from '@/lib/supabase';

export type ActivityStatus =
  | 'cancelled'
  | 'draft'
  | 'submitted'
  | 'scheduled'
  | 'out_for_delivery'
  | 'delivered'
  | 'invoiced'
  | 'invoiced_paid'
  | 'unknown';

export interface ActivityRow {
  /** Opaque composite key from the view ('order:<uuid>'). Good for React keys
   *  and dedupe; NOT an order id — see `orderId`. */
  rowId: string;
  /** The actual orders.orders id, or null on an invoice-sourced row. */
  orderId: string | null;
  orderNumber: string;
  sfJobId: string | null;
  qboInvoiceId: string | null;
  invoiceDocNumber: string | null;
  activityDate: string;
  scheduledDate: string | null;
  status: ActivityStatus;
  amount: number;
  source: 'order' | 'invoice';
}

interface ActivityDbRow {
  row_id: string;
  order_number: string;
  sf_job_id: string | null;
  qbo_invoice_id: string | null;
  invoice_doc_number: string | null;
  activity_date: string;
  scheduled_date: string | null;
  status: string;
  amount: number | string;
  source: string;
}

const SELECT_COLS =
  'row_id, order_number, sf_job_id, qbo_invoice_id, invoice_doc_number, activity_date, scheduled_date, status, amount, source';

function normalizeStatus(s: string): ActivityStatus {
  const known: ActivityStatus[] = [
    'cancelled',
    'draft',
    'submitted',
    'scheduled',
    'out_for_delivery',
    'delivered',
    'invoiced',
    'invoiced_paid',
  ];
  return (known as readonly string[]).includes(s) ? (s as ActivityStatus) : 'unknown';
}

/** Unified order + invoice activity feed for the current customer. */
export function useActivity() {
  return useQuery<ActivityRow[]>({
    queryKey: ['activity', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_activity')
        .select(SELECT_COLS)
        .order('activity_date', { ascending: false });
      /* ⚠ A failed read used to return [] silently, so an RLS refusal, a bad
         column or a dropped connection all rendered as "No orders yet — orders
         you place here will show up…" — an account with 200 orders reading as a
         brand-new one, with nothing on screen or in the console to say why.
         Same swallow that hid the composite-key 400 and the EDI intake's 401. */
      if (error) {
        console.error('[useActivity] could not read the activity feed:', error);
        throw new Error(error.message || 'Could not load your order history.');
      }
      return (data ?? []).map((r): ActivityRow => {
        const row = r as ActivityDbRow;
        return {
          rowId: row.row_id,
          orderId: activityOrderId(row.row_id),
          orderNumber: row.order_number,
          sfJobId: row.sf_job_id,
          qboInvoiceId: row.qbo_invoice_id,
          invoiceDocNumber: row.invoice_doc_number,
          activityDate: row.activity_date,
          scheduledDate: row.scheduled_date,
          status: normalizeStatus(row.status),
          amount: Number(row.amount ?? 0),
          source: row.source === 'invoice' ? 'invoice' : 'order',
        };
      });
    },
    staleTime: 60 * 1000,
  });
}
